import React, { useEffect, useState } from "react";
import { FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import axios from "axios";
import "../styles/GlobalStyles.css";

const SkillsSection = ({ onSuccess, initialData, onClose }) => {
  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedSkill, setEditedSkill] = useState("");

  /* ================= LOAD SKILLS ================= */
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/profile/getCandProfile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const rawSkills = res.data?.candidate?.skills || [];

        // Backend stores: ["React", "Node"] or objects
        const parsed = rawSkills.map((s) => {
          if (typeof s === "string") {
            // Remove any legacy level appended with dash if it exists
            return { name: s.split('-')[0].trim() };
          } else if (typeof s === "object" && s !== null) {
            return {
              name: s.skill || s.name || "",
              _id: s._id
            };
          }
          return { name: String(s) };
        });

        setSkills(parsed);

        // Auto-open edit if initialData (specific skill) is passed
        if (initialData && typeof initialData === 'object') {
          const targetId = initialData._id;
          const targetName = initialData.skill || initialData.name;

          const foundIndex = parsed.findIndex(p =>
            (targetId && p._id === targetId) || (p.name === targetName)
          );

          if (foundIndex !== -1) {
            setEditingIndex(foundIndex);
            setEditedSkill(parsed[foundIndex].name);
          }
        }

      } catch (err) {
        console.error("Failed to load skills", err);
      }
    };

    fetchSkills();
  }, [initialData]);

  /* ================= CRUD ================= */

  const addSkill = () => {
    if (!newSkill.trim()) return;

    if (skills.some((s) => s.name.toLowerCase() === newSkill.toLowerCase())) {
      alert("Skill already added");
      return;
    }

    setSkills([...skills, { name: newSkill }]);
    setNewSkill("");
  };

  const deleteSkill = (index) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  const startEditing = (index) => {
    setEditingIndex(index);
    setEditedSkill(skills[index].name);
  };

  const saveEdit = () => {
    const updated = [...skills];
    updated[editingIndex] = { name: editedSkill };
    setSkills(updated);
    setEditingIndex(null);
  };

  const cancelEdit = () => setEditingIndex(null);

  /* ================= SAVE TO BACKEND ================= */

  const saveSkillsToBackend = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return alert("Please login to save skills");

      // ✅ CONVERT TO STRING ARRAY FOR BACKEND
      const payload = skills.map((s) => s.name).filter(s => s && s.trim());

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/profile/update/skills`,
        { skills: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (onSuccess) onSuccess();
      if (onClose) onClose();
      window.location.reload(); // Reload to reflect changes on profile page
    } catch (err) {
      console.error(err);
      alert("Failed to save skills.");
    }
  };


  /* ================= UI ================= */

  return (
    <div className="profile-card page-container">
      <h2 className="page-title">Skills</h2>

      {/* Skills List */}
      <div className="skills-list">
        {skills.length > 0 ? (
          skills.map((skill, index) => (
            <div
              key={index}
              className="skill-item skill-item-hover"
              onClick={() => startEditing(index)}
            >
              {editingIndex === index ? (
                <>
                  <input
                    className="profile-input"
                    value={editedSkill}
                    onChange={(e) => setEditedSkill(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />

                  <div className="skill-actions">
                    <FaCheck
                      className="icon-success"
                      onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                      title="Save"
                    />
                    <FaTimes
                      className="icon-danger"
                      onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                      title="Cancel"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <strong>{skill.name}</strong>
                  </div>

                  {/* ✅ DELETE ONLY INSIDE CARD */}
                  <div className="skill-actions">
                    <FaTrash
                      className="icon-danger"
                      onClick={(e) => { e.stopPropagation(); deleteSkill(index); }}
                      title="Delete skill"
                    />
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <p className="empty-text">No skills added yet.</p>
        )}
      </div>


      {/* Add Skill */}
      <div className="skill-form">
        <div className="form-group">
          <label className="profile-label">Skill</label>
          <input
            className="profile-input"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            placeholder="Enter skill"
          />
        </div>

        <button className="save-button" onClick={addSkill}>
          Add Skill
        </button>
      </div>

      <button className="save-button" onClick={saveSkillsToBackend}>
        Save All Skills
      </button>
    </div>
  );
};

export default SkillsSection;
